import { isDefined } from "@clipboard-health/util-ts";
import { environmentConfig } from "@src/appV2/environment";
import { useQueries } from "@tanstack/react-query";

import { get, type UseGetQueryOptions } from "../api";
import { APP_V2_APP_EVENTS } from "../lib";
import {
  InstantPayUser,
  type TimeCardFilesType,
  type TimesheetResponse,
  timesheetSchema,
  TimesheetType,
} from "./types";

export function isHcpSignature(file?: Pick<TimeCardFilesType, "timesheet" | "signedType">) {
  return file?.timesheet === TimesheetType.PHOTO && file?.signedType === InstantPayUser.HCP;
}

export async function getTimesheetDistributionUrl(
  fileStorageFileKey: string,
  shiftId: string
): Promise<TimesheetResponse & { fileStorageFileKey: string; shiftId: string }> {
  const response = await get({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/shift/${shiftId}/timesheet/${fileStorageFileKey}/distribution-url`,
    responseSchema: timesheetSchema,
  });
  return {
    ...response.data,
    fileStorageFileKey,
    shiftId,
  };
}

export function useGetTimesheetURLs(
  fileStorageFileKeys: string[],
  shiftId: string,
  options?: UseGetQueryOptions<TimesheetResponse & { fileStorageFileKey: string; shiftId: string }>
) {
  const results = useQueries({
    queries: fileStorageFileKeys.map((fileStorageFileKey) => ({
      queryKey: [shiftId, fileStorageFileKey],
      queryFn: async () => await getTimesheetDistributionUrl(fileStorageFileKey, shiftId),
      staleTime: 2 * 60 * 1000,
      ...options,
      meta: {
        logErrorMessage: APP_V2_APP_EVENTS.GET_TIMESHEET_URL,
      },
      enabled: isDefined(fileStorageFileKey) && options?.enabled,
    })),
  });

  const isLoading = results.some((result) => result.status === "loading");
  const isSuccess = results.every((result) => result.status === "success");
  const isError = results.some((result) => result.status === "error");

  const data = results.map((result) => result.data);
  const errors = results.map((result) => result.error);

  return {
    data,
    errors,
    isLoading,
    isSuccess,
    isError,
  };
}
